import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useDragControls } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';

import { 
    Wifi, ChevronDown, MoreHorizontal, X, Minus, Maximize2, ArrowLeft, User
} from 'lucide-react';

import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import useFieldMasking from '../../../pages/Master_Settings/useFieldMasking';
import './CallWidget.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { getSession } from '../../../getSession';

const getAvatarStyle = (name) => {
    const palettes = [
        { bg: '#eff6ff', text: '#3b82f6', border: '#dbeafe' }, // Blue
        { bg: '#ecfdf5', text: '#10b981', border: '#d1fae5' }, // Green
        { bg: '#fff1f2', text: '#f43f5e', border: '#ffe4e6' }, // Rose
        { bg: '#fffbeb', text: '#f59e0b', border: '#fef3c7' }, // Amber
        { bg: '#f5f3ff', text: '#8b5cf6', border: '#ede9fe' }, // Violet
        { bg: '#ecfeff', text: '#06b6d4', border: '#cffafe' }  // Cyan
    ];
    const charCode = (name || 'U').charCodeAt(0);
    return palettes[charCode % palettes.length];
};

const getInitials = (name) => {
    if (!name) return '??';
    const str = name.toString().trim();
    
    // Number logic: first 2 digits (ignoring +)
    if (/^\+?\d[\d\s-]*$/.test(str)) {
        const digits = str.replace(/\D/g, '');
        return digits.substring(0, 2);
    }
    
    const parts = str.split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
};


const CallWidget = ({ 
    show, 
    onClose, 
    toMobileNo, 
    entityId, 
    entityName, 
    personName, 
    personImage,
    toEmail,
    extraContactInfo,
    triggerRect,
    pageCode
}) => {
    const { maskObject } = useFieldMasking(pageCode || 'PAG10372#PAG10039');
    const [callStatus, setCallStatus] = useState('Submitted');
    const [callTimer, setCallTimer] = useState(0);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isBottomLeft, setIsBottomLeft] = useState(false);
    const [showExtras, setShowExtras] = useState(false);
    const [callData, setCallData] = useState(null);
    const [uniqueId, setUniqueId] = useState(null);
    const [isDisconnected, setIsDisconnected] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [activeActionMode, setActiveActionMode] = useState(null);

    const dragControls = useDragControls();
    const widgetRef = useRef(null);

    // Persist position
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const handleDragEnd = (event, info) => {
        // Update cumulative offset
        const newOffset = {
            x: dragOffset.x + info.offset.x,
            y: dragOffset.y + info.offset.y
        };
        setDragOffset(newOffset);
    };

    // Mapping icons to LeadActionOperation modes
    const modeMapping = {
        'Followup': 'followup',
        'Email': 'mail',
        'SMS': 'sms',
        'Voice': 'voice',
        'Note': 'notes',
        'Document': 'upload-document',
        'Add Task': 'add-task',
        'Add Appointment': 'add-appointment',
        'Add Deal': 'add-deal',
        'WhatsApp': 'whatsapp',
        'Webform': 'fill-webform'
    };

    const handleActionClick = (label) => {
        const mode = modeMapping[label];
        if (mode) {
            setActiveActionMode(mode);
        }
    };

    const formatCallTime = (seconds) => {
        const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const ss = String(seconds % 60).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    };

    const handleCallInitiation = useCallback(async () => {
        const agentId = localStorage.getItem('UserId') || '34594';
        const payload = {
            toMobileNo: toMobileNo,
            entityId: String(entityId),
            entityName: entityName || 'lead',
            agentId: parseInt(agentId, 10)
        };

        try {
            const response = await fetch('https://tpc.kit19.com/api/ClickToCall/GetEndpointSetting', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            
            if (result.status && result.msg) {
                try {
                    const parsedMsg = JSON.parse(result.msg);
                    setCallData(parsedMsg);
                    setUniqueId(result.uniqueIdentity);
                } catch (e) {
                    console.error('Error parsing msg:', e);
                }
            }
            
            // Simulate connection transition
            setTimeout(() => {
                setCallStatus(prev => (prev === 'Disconnected' || prev === 'Failed to connect') ? prev : 'Connecting...');
            }, 3000);
        } catch (error) {
            console.error('Error initiating call:', error);
            setCallStatus('Failed to connect');
        }
    }, [toMobileNo, entityId, entityName]);

    const handleReportCall = async () => {
        if (!uniqueId) return;

        setIsDisconnected(true);
        setCallStatus('Disconnected');
        setCallTimer(0);
        setShowExtras(false);

        const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
        const Report_Call_Ended = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.REPORT_CALL_ENDED_TIME}`;
        const Update_Call_Failed_Status = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.UPDATE_CALL_FAILED_STATUS}`;

        try {
            // First hit: Report Call Ended
            const reportPayload = {
                Token: "-2295521862261168",
                Details: {
                    EndpontUniqueId: parseInt(uniqueId, 10)
                }
            };
            await fetch(Report_Call_Ended, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(reportPayload)
            });

            // Second hit: Update Call Failed Status
            const updatePayload = {
                Token: "-2295521862261168",
                Details: {
                    UniqueIdentity: parseInt(uniqueId, 10),
                    Mode: "END"
                }
            };
            await fetch(Update_Call_Failed_Status, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatePayload)
            });
        } catch (error) {
            console.error('Error reporting call:', error);
        }
    };

    const handleAutoDisconnect = useCallback(async () => {
        if (!uniqueId || isDisconnected) return;

        setIsDisconnected(true);
        setCallStatus('Disconnected');
        setCallTimer(0);
        setShowExtras(false);

        const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
        const Update_Call_Failed_Status = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.UPDATE_CALL_FAILED_STATUS}`;

        try {
            // Automatically hit ONLY Update Call Failed Status
            const updatePayload = {
                Token: "-2295521862261168",
                Details: {
                    UniqueIdentity: parseInt(uniqueId, 10),
                    Mode: "END"
                }
            };
            await fetch(Update_Call_Failed_Status, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatePayload)
            });
        } catch (error) {
            console.error('Error in auto-disconnect:', error);
        }
    }, [uniqueId, isDisconnected]);

    useEffect(() => {
        if (show) {
            setDragOffset({ x: 0, y: 0 });
            setCallTimer(0);
            setCallStatus('Submitted');
            setIsDisconnected(false);
            setShowExtras(false);
            setIsMinimized(false);
            setCountdown(5);
            setActiveActionMode(null);
            handleCallInitiation();
        }
    }, [show, handleCallInitiation]);

    useEffect(() => {
        let interval;
        if (show && !isMinimized && !isDisconnected && callStatus !== 'Failed to connect') {
            interval = setInterval(() => {
                setCallTimer(prev => {
                    const next = prev + 1;
                    if (next === 59) {
                        handleAutoDisconnect();
                    }
                    return next;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [show, isMinimized, isDisconnected, callStatus, handleAutoDisconnect]);

    // Countdown logic for closure
    useEffect(() => {
        let timer;
        if (isDisconnected && countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (isDisconnected && countdown === 0) {
            onClose();
        }
        return () => clearInterval(timer);
    }, [isDisconnected, countdown, onClose]);

    if (!show) return null;

    const extras = [
        { icon: <CalendarTodayIcon sx={{ color: '#3b82f6', fontSize: 24 }} />, label: 'Followup' },
        { icon: <EmailIcon sx={{ color: '#ef4444', fontSize: 24 }} />, label: 'Email' },
        { icon: <SmsIcon sx={{ color: '#10b981', fontSize: 24 }} />, label: 'SMS' },
        { icon: <SettingsVoiceIcon sx={{ color: '#8b5cf6', fontSize: 24 }} />, label: 'Voice' },
        { icon: <NoteAddIcon sx={{ color: '#f59e0b', fontSize: 24 }} />, label: 'Note' },
        { icon: <DescriptionIcon sx={{ color: '#06b6d4', fontSize: 24 }} />, label: 'Document' },
        { icon: <AssignmentIcon sx={{ color: '#6366f1', fontSize: 24 }} />, label: 'Add Task' },
        { icon: <EventAvailableIcon sx={{ color: '#ec4899', fontSize: 24 }} />, label: 'Add Appointment' },
        { icon: <HandshakeIcon sx={{ color: '#f97316', fontSize: 24 }} />, label: 'Add Deal' },
        { icon: <WhatsAppIcon sx={{ color: '#25d366', fontSize: 24 }} />, label: 'WhatsApp' },
        { icon: <ViewCompactIcon sx={{ color: '#64748b', fontSize: 24 }} />, label: 'Webform' }
    ];



    const getPositionStyle = () => {
        if (isFullScreen) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bottom: 'auto', right: 'auto', zIndex: 999999 };
        if (isBottomLeft) return { bottom: '24px', left: '24px', top: 'auto', right: 'auto', zIndex: 999999 };
        
        if (triggerRect) {
            return {
                top: `${triggerRect.bottom + 8}px`,
                left: `${triggerRect.left}px`,
                bottom: 'auto',
                right: 'auto',
                zIndex: 999999
            };
        }
        return { bottom: '24px', right: '24px', top: 'auto', left: 'auto', zIndex: 999999 };
    };

    return (
        <motion.div 
            ref={widgetRef}
            drag
            dragControls={dragControls}
            dragListener={false}
            dragMomentum={false}
            dragElastic={0}
            onDragEnd={handleDragEnd}
            initial={dragOffset}
            animate={dragOffset}
            whileDrag={{ scale: 1.01, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
            className={`central-call-widget ${isMinimized ? 'minimized' : ''} ${isFullScreen ? 'full-screen' : ''} ${isBottomLeft ? 'bottom-left' : ''}`}
            style={getPositionStyle()}
        >
            <div className="call-widget-header" onPointerDown={(e) => dragControls.start(e)} style={{ cursor: 'move' }}>
                <h3>Call Widget (Kit19 80)</h3>
                <div className="header-actions" onPointerDown={(e) => e.stopPropagation()}>
                    <button className="header-btn" onClick={() => {
                        setIsBottomLeft(!isBottomLeft);
                        if (!isBottomLeft) setIsMinimized(true); // Collapse on minimize
                    }} title="Minimize to bottom-left">
                        <Minus size={14} />
                    </button>
                    <button className="header-btn" onClick={() => setIsFullScreen(!isFullScreen)} title="Full Screen">
                        <Maximize2 size={14} />
                    </button>
                    <button className="header-btn" onClick={() => setIsMinimized(!isMinimized)} title={isMinimized ? "Expand" : "Collapse"}>
                        <ChevronDown size={14} style={{ transform: isMinimized ? 'rotate(180deg)' : 'none' }} />
                    </button>
                    <button className="widget-close-btn" onClick={onClose} title="Close">
                        <X size={14} />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <div className="call-widget-body">
                    {activeActionMode ? (
                        <div className="widget-action-wrapper">
                            <div className="action-back-nav" onClick={() => setActiveActionMode(null)}>
                                <ArrowLeft size={16} />
                                <span>Back to options</span>
                            </div>
                            <div className="mini-operation-container">
                                <LeadActionOperation 
                                    mode={activeActionMode}
                                    entity={{
                                        leadId: entityId,
                                        enquiryId: entityId,
                                        id: entityId,
                                        PersonName: personName || entityName,
                                        MobileNo: toMobileNo,
                                        EmailID: toEmail,
                                        ...(extraContactInfo || {})
                                    }}
                                    onBack={() => setActiveActionMode(null)}
                                    onSuccess={() => setActiveActionMode(null)}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="more-options" onClick={() => setShowExtras(!showExtras)} style={{ cursor: 'pointer' }}>
                                <MoreHorizontal size={20} color={showExtras ? '#0ea5e9' : '#333'} />
                            </div>

                            {showExtras && !isDisconnected && (
                                <div className="call-status-section top">
                                    <p className="call-timer">{formatCallTime(callTimer)}</p>
                                </div>
                            )}

                            {showExtras && !isDisconnected ? (
                                <div className="extras-grid">
                                    {extras.map((extra, idx) => (
                                        <button 
                                            key={idx} 
                                            className="extra-btn" 
                                            title={extra.label}
                                            onClick={() => handleActionClick(extra.label)}
                                        >
                                            {extra.icon}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="profile-section">
                                    {/* Agent Profile Card */}
                                    <div className="profile-card agent-card">
                                        <div className="profile-avatar bg-slate-100 flex items-center justify-center border border-slate-200">
                                            <User className="w-8 h-8 text-slate-400" />
                                        </div>
                                        {callData ? (
                                            <>
                                                <span className="profile-name">
                                                    {maskObject({ PersonName: callData?.AgentName || 'Agent' }).PersonName}
                                                </span>
                                                <span className="profile-number">
                                                    {maskObject({ MobileNo: callData?.MobileNo || '...' }).MobileNo}
                                                </span>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', alignItems: 'center' }}>
                                                <div style={{ height: '14px', width: '60%', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                                                <div style={{ height: '12px', width: '80%', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Connection Icon (Green if connected, Red if disconnected) */}
                                    <div className={`connection-icon ${isDisconnected ? 'disconnected' : 'animate-pulse-green'}`}>
                                        <Wifi size={24} />
                                    </div>

                                    {/* Customer Profile Card */}
                                    <div className="profile-card lead-card">
                                        {(() => {
                                            const customerName = callData?.CustomerName || personName || toMobileNo || 'Customer';
                                            const style = getAvatarStyle(customerName);
                                            return (
                                                <div 
                                                    className="profile-avatar profile-avatar-pulse"
                                                    style={{ 
                                                        backgroundColor: style.bg, 
                                                        color: style.text,
                                                        border: `2px solid ${style.border}`
                                                    }}
                                                >
                                                    {getInitials(customerName)}
                                                </div>
                                            );
                                        })()}
                                        
                                        {callData ? (
                                            <>
                                                <span className="profile-name">
                                                    {maskObject({ PersonName: callData?.CustomerName || personName || 'Customer' }).PersonName}
                                                </span>
                                                <span className="profile-number">
                                                    {maskObject({ MobileNo: callData?.toMobileNo || toMobileNo }).MobileNo}
                                                </span>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', alignItems: 'center' }}>
                                                <div style={{ height: '14px', width: '60%', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                                                <div style={{ height: '12px', width: '80%', backgroundColor: '#e2e8f0', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            )}

                            {(!showExtras || isDisconnected) && (
                                <div className="call-status-section">
                                    <p className="status-label">{callStatus}</p>
                                    <p className="call-timer">{formatCallTime(callTimer)}</p>
                                </div>
                            )}
                        </>
                    )}

                    {!isDisconnected ? (
                        <div className="call-widget-footer">
                            <span>Call Disconnected ?</span>
                            <span className="report-btn" onClick={handleReportCall}>Click to report</span>
                        </div>
                    ) : (
                        <div className="call-widget-footer closing">
                            <span>Closing In <span className="countdown-value">{countdown} Sec</span></span>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export default CallWidget;
